import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class SmartPreloadingStrategy implements PreloadingStrategy {
    private isInitialLoadComplete = false;
    private preloadQueue: Array<{ route: Route; load: () => Observable<any> }> = [];

    // Critical routes that should be preloaded immediately after initial load
    private criticalRoutes = [
        'cart',
        'checkout'
    ];

    // High priority routes (preload after critical)
    private highPriorityRoutes = [
        'products',
        'category'
    ];

    // Medium priority routes (preload when idle)
    private mediumPriorityRoutes = [
        'account',
        'auth'
    ];

    // Low priority routes (preload only when browser is idle)
    private lowPriorityRoutes = [
        'warranty',
        'privacy-policy',
        'shops',
        'about-us'
    ];

    preload(route: Route, load: () => Observable<any>): Observable<any> {
        const routePath = route.path || '';
        const routeData = route.data as any;

        // Check if route explicitly has preload: false
        if (routeData?.preload === false) {
            return of(null);
        }

        // Check if route should be preloaded based on priority
        if (this.shouldPreloadRoute(routePath, routeData)) {
            return this.preloadWithStrategy(route, load, routePath);
        }

        return of(null);
    }

    private preloadWithStrategy(route: Route, load: () => Observable<any>, routePath: string): Observable<any> {
        // Critical routes: preload immediately
        if (this.criticalRoutes.some(r => routePath.includes(r))) {
            return load();
        }

        // High priority routes: preload after initial load
        if (this.highPriorityRoutes.some(r => routePath.includes(r))) {
            if (this.isInitialLoadComplete) {
                return load();
            } else {
                // Queue for later preloading
                this.preloadQueue.push({ route, load });
                return of(null);
            }
        }

        // Medium priority routes: preload with delay
        if (this.mediumPriorityRoutes.some(r => routePath.includes(r))) {
            return timer(1000).pipe(
                switchMap(() => load()),
                take(1)
            );
        }

        // Low priority routes: preload when idle
        if (this.lowPriorityRoutes.some(r => routePath.includes(r))) {
            return this.preloadWhenIdle(load);
        }

        return of(null);
    }

    private shouldPreloadRoute(routePath: string, routeData: any): boolean {
        // Check explicit preload setting in route data
        if (routeData?.preload === true) {
            return true;
        }

        // Check if route is in any priority category
        return this.criticalRoutes.some(r => routePath.includes(r)) ||
            this.highPriorityRoutes.some(r => routePath.includes(r)) ||
            this.mediumPriorityRoutes.some(r => routePath.includes(r)) ||
            this.lowPriorityRoutes.some(r => routePath.includes(r));
    }

    private preloadWhenIdle(load: () => Observable<any>): Observable<any> {
        // Use requestIdleCallback for browser idle time
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            return new Observable((observer: any) => {
                (window as any).requestIdleCallback(() => {
                    load().subscribe(observer);
                }, { timeout: 5000 });
            });
        } else {
            // Fallback: delay for 3 seconds
            return timer(3000).pipe(
                switchMap(() => load()),
                take(1)
            );
        }
    }

    // Method to mark initial load as complete and process queued routes
    public markInitialLoadComplete(): void {
        this.isInitialLoadComplete = true;

        // Process queued high priority routes
        this.preloadQueue.forEach(({ route, load }) => {
            const routePath = route.path || '';
            if (this.highPriorityRoutes.some(r => routePath.includes(r))) {
                load().subscribe();
            }
        });

        this.preloadQueue = [];
    }
}
